import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';
import FlipNumber from './FlipNumber';

export default function HeroSection() {
  const navigate = useNavigate();
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoPhase, setVideoPhase] = useState<'idle' | 'flash' | 'playing'>('idle');
  const [objPos, setObjPos] = useState({ x: 50, y: 0 });
  const [wakuActive, setWakuActive] = useState(false);

  // 「ワクワク」クリック → 色が変わって、工事記録ページへ
  const handleWakuClick = useCallback(() => {
    if (wakuActive) return;
    setWakuActive(true);
    window.setTimeout(() => navigate('/wakuwaku-studio-log'), 380);
  }, [wakuActive, navigate]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-x-0');
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setObjPos({ x, y });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setObjPos({ x: 50, y: 0 });
  }, []);

  const handleVideoOpen = useCallback(() => {
    setVideoOpen(true);
    setVideoPhase('flash');
    setTimeout(() => setVideoPhase('playing'), 500);
  }, []);

  const handleVideoClose = useCallback(() => {
    setVideoOpen(false);
    setVideoPhase('idle');
  }, []);

  // キーボードでビデオを閉じる
  useEffect(() => {
    if (!videoOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleVideoClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [videoOpen, handleVideoClose]);

  return (
    <>
      <section id="home" className="relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-site-light">

        {/* ── 左カラム：テキスト ── */}
        <div className="relative z-10 flex flex-col justify-center px-10 md:px-16 lg:px-24 py-24 md:py-0 md:w-[52%] lg:w-[48%]">
          <span className="absolute left-8 top-1/2 -translate-y-1/2 hidden lg:block w-[2px] h-32 bg-gradient-to-b from-transparent via-teal-400 to-transparent" />
          <p className="eyebrow text-teal-400 text-sm md:text-base mb-6">
            Sound Creator — Minoh, Osaka
          </p>
          <h1 className="text-white font-mincho leading-snug mb-8">
            <span className="block text-5xl md:text-7xl lg:text-8xl">音で、</span>
            <span className="block text-5xl md:text-7xl lg:text-8xl">世界を</span>
            <button
              type="button"
              onClick={handleWakuClick}
              aria-label="WakuWaku Studio 工事記録のページへ移動"
              className="group relative block text-left text-5xl md:text-7xl lg:text-8xl font-mincho transition-transform duration-300 ease-out cursor-pointer hover:scale-[1.03]"
            >
              <span
                className={`transition-all duration-300 ease-out ${
                  wakuActive
                    ? 'text-white'
                    : 'text-teal-400 group-hover:text-white group-hover:drop-shadow-[0_0_22px_rgba(45,212,191,0.85)]'
                }`}
              >
                ワクワク
              </span>
              {/* クリック/ホバーで伸びる下線 */}
              <span
                className={`pointer-events-none absolute -bottom-1 left-0 h-[2px] bg-current transition-all duration-500 ease-out ${
                  wakuActive ? 'w-full opacity-100' : 'w-0 opacity-0 group-hover:w-full group-hover:opacity-60'
                }`}
              />
              {/* クリック可能ヒント（デスクトップのみ） */}
              <span className="pointer-events-none absolute left-full top-1/2 hidden -translate-y-1/2 whitespace-nowrap pl-4 text-[10px] md:inline-block tracking-[0.35em] text-teal-300/0 transition-colors duration-300 group-hover:text-teal-300/80">
                STUDIO LOG →
              </span>
            </button>
            <span className="block text-5xl md:text-7xl lg:text-8xl">させる。</span>
          </h1>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-[1px] bg-teal-400" />
            <div className="w-2 h-2 rounded-full bg-teal-400" />
          </div>
          <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-1 mb-10 max-w-sm">
            <p>アイデアが<span className="text-teal-300 font-semibold">湧き</span>、創造が<span className="text-teal-300 font-semibold">躍動</span>する。</p>
            <p className="text-gray-400 text-sm">
              映像・空間・人の心を"音"でつなぐ<br />
              合同会社 WakuWaku
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="primary" size="lg" onClick={() => document.querySelector('#works')?.scrollIntoView({ behavior: 'smooth' })}>
              <i className="ri-play-circle-line mr-2" />最新作品を見る
            </Button>
            <Button variant="outline" size="lg" onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}>
              <i className="ri-mail-line mr-2" />お問い合わせ
            </Button>
          </div>
          <div className="mt-16 flex items-center gap-3 text-gray-500 text-xs tracking-widest">
            <span className="w-8 h-[1px] bg-gray-600" />
            <span>SCROLL</span>
            <i className="ri-arrow-down-line animate-bounce" />
          </div>
        </div>

        {/* ── 右カラム：画像（マウス追従＋クリックでフルスクリーン） ── */}
        <div
          ref={containerRef}
          className="relative md:w-[48%] lg:w-[52%] flex items-center justify-center py-16 md:py-0 cursor-zoom-in group"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleVideoOpen}
        >
          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[#0d0d0f] to-transparent z-10 pointer-events-none" />

          <img
            ref={imgRef}
            src="/images/hero/expo2025.jpg"
            alt="WakuWaku Sound"
            className="relative z-0 w-[80%] max-w-[420px] h-auto object-contain rounded-xl opacity-0 translate-x-8 transition-[opacity,transform] duration-500 ease-out"
            style={{ transitionProperty: 'opacity, transform' }}
          />

          {/* ホバー時：クリックヒント */}
          <div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-black/50 backdrop-blur-sm rounded-full px-5 py-2 text-white text-xs flex items-center gap-2">
              <i className="ri-play-circle-line text-teal-400" />
              クリックでプロモーション映像を再生
            </div>
          </div>

          {/* 右下バッジ */}
          <div className="absolute bottom-10 right-8 z-20 bg-black/60 backdrop-blur-sm border border-teal-400/30 rounded-lg px-4 py-3 text-white text-xs">
            <FlipNumber value={28} suffix="+" duration={1800} triggerOnView className="text-base font-light" />
            <p className="text-gray-300 leading-tight">年の<br />現場経験</p>
          </div>
        </div>

      </section>

      {/* ── ビデオモーダル ── */}
      {videoOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* フラッシュエフェクト */}
          <div
            className={`absolute inset-0 z-10 transition-all duration-700 ease-out ${
              videoPhase === 'flash'
                ? 'bg-teal-400/40'
                : 'bg-teal-400/0 pointer-events-none'
            }`}
          >
            {/* 光のライン */}
            <div
              className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transition-all duration-700 ease-out ${
                videoPhase === 'flash' ? 'translate-x-0' : '-translate-x-full'
              }`}
            />
            {/* パーティクル風のドット */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className={`absolute w-1 h-1 rounded-full bg-white transition-all duration-1000 ease-out ${
                    videoPhase === 'flash' ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                  }`}
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    top: `${10 + Math.random() * 80}%`,
                    transitionDelay: `${i * 30}ms`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* ビデオプレイヤー（フラッシュ後に表示） */}
          <div
            className={`absolute inset-0 z-20 flex items-center justify-center bg-black transition-all duration-500 ${
              videoPhase === 'playing' ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
            onClick={handleVideoClose}
          >
            {/* 閉じるボタン */}
            <button
              className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all hover:scale-110 cursor-pointer whitespace-nowrap z-30"
              onClick={handleVideoClose}
            >
              <i className="ri-close-line text-xl" />
            </button>

            {/* タイトル */}
            <p className="absolute top-6 left-6 text-white/70 text-sm tracking-wider z-30">
              <i className="ri-movie-line mr-2 text-teal-400" />
              WakuWaku プロモーション映像
            </p>

            {/* 操作ヒント */}
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-xs tracking-widest z-30">
              ESC で閉じる
            </p>

            {/* YouTube 埋め込み */}
            <div
              className="relative w-full max-w-5xl aspect-video mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={`https://www.youtube.com/embed/fGm6QvQksE0?autoplay=1&rel=0&modestbranding=1${videoPhase === 'playing' ? '' : ''}`}
                title="WakuWaku プロモーション映像"
                className="w-full h-full rounded-xl shadow-2xl shadow-teal-400/20"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
